import type { AttentionLevel, RecurrenceRule, Task } from '@/src/renderer/lib/db/models'
import { recurrenceUpdate } from '@/src/renderer/lib/recurrence'
import type { StartValue } from '../WhenDropdown'
import type { DurationValue } from '../TaskEntry/TaskEntry.types'
import type { TaskMenuHandlers } from './TaskMenu.types'

type TaskChanges = Partial<Omit<Task, 'id'>>

/**
 * Was eine offene Karte zusätzlich mitschreibt: ihr Formular, damit das Aufgeklappte gleich
 * stimmt. Die **Listen-Werkzeug-Pille** hat keins — bei Mehrfachauswahl ist keine Karte offen.
 */
export interface TaskMenuFormSetters {
  setStart: (value: StartValue | null) => void
  setDuration: (value: DurationValue | null) => void
  setAttention: (value: AttentionLevel | null) => void
  setRecurrence: (rule: RecurrenceRule | null) => void
}

/**
 * Was die Kästen des **Aufgaben-Menüs** zurückmelden, übersetzt in Felder der Aufgabe — EIN Weg für
 * die Zeile (Rechtsklick, Kürzel) und die Pille bei Mehrfachauswahl (#291). Wohin geschrieben wird,
 * sagt `apply`.
 */
export function taskMenuHandlers(apply: (changes: TaskChanges) => void, form?: TaskMenuFormSetters): TaskMenuHandlers {
  return {
    onStartChange: (value) => {
      form?.setStart(value)
      apply({
        startDate: value?.date ?? null,
        startTime: value?.time ?? null,
        startPeriod: value?.period ?? null,
      })
    },
    onDurationChange: (value) => {
      form?.setDuration(value)
      apply({
        estimatedDuration: value?.minutes ?? null,
        durationMode: value?.mode ?? null,
      })
    },
    onDeadlineChange: (value) => {
      apply({
        dueDate: value?.date ?? null,
        dueTime: value?.time ?? null,
        duePeriod: value?.period ?? null,
      })
    },
    onAttentionChange: (value) => {
      form?.setAttention(value)
      apply({ attention: value })
    },
    onTagsChange: (value) => {
      apply({ tags: value })
    },
    onRewardChange: (rewardId) => {
      apply({ rewardId })
    },
    onRecurrenceChange: (rule) => {
      // Auch ins Formular: Bei geöffneter Karte liest die Pille unten links von dort.
      form?.setRecurrence(rule)
      const patch = recurrenceUpdate(rule, new Date())
      if (patch.startDate) form?.setStart({ date: patch.startDate, time: null, period: null })
      apply(patch)
    },
  }
}
